import { useEffect, useMemo, useState } from 'react';
import {
  useKlantenStore,
  type MergeAddressFields,
  type MergeCandidate,
  type MergeCandidatesResult,
} from '../../stores/klanten-store';
import { formatDate } from '../../lib/format';

interface Props {
  isOpen: boolean;
  sourceAccountId: string;
  sourceName: string;
  onClose: () => void;
  /** Aangeroepen na een geslaagde merge met de Id van het overgebleven master-account. */
  onMerged: (masterId: string) => void;
}

const ADDRESS_LABELS: Record<keyof MergeAddressFields, string> = {
  BillingStreet: 'Straat',
  BillingPostalCode: 'Postcode',
  BillingCity: 'Plaats',
  BillingCountry: 'Land',
  BillingAddress_toevoeging__c: 'Toevoeging',
};
const ADDRESS_KEYS = Object.keys(ADDRESS_LABELS) as (keyof MergeAddressFields)[];

const SIGNAL_LABELS: Record<NonNullable<MergeCandidate['signal']>, string> = {
  phone_and_email: 'E-mail + telefoon',
  email: 'Zelfde e-mail',
  phone: 'Zelfde telefoon',
};

function countsLine(counts: { orders: number; invoices: number; cases: number; opportunities: number }): string {
  const parts: string[] = [];
  parts.push(`${counts.orders} orders`);
  parts.push(`${counts.invoices} facturen`);
  if (counts.cases > 0) parts.push(`${counts.cases} cases`);
  if (counts.opportunities > 0) parts.push(`${counts.opportunities} offertes`);
  return parts.join(' · ');
}

/**
 * Dialog om twee dubbele PersonAccounts samen te voegen. Toont kandidaten,
 * laat de master kiezen, previewt de survivorship voor het (billing-)adres en
 * bevestigt de merge. Admin-only — de knop die dit opent staat al achter een
 * role-gate en de server handhaaft requireRole(['admin']).
 *
 * v1 = pairwise: precies de bron + één gekozen kandidaat. De master wint;
 * ontbrekende master-adresvelden kunnen bewust van het duplicaat worden
 * overgenomen (fieldOverrides).
 */
export function MergeDialog({ isOpen, sourceAccountId, sourceName, onClose, onMerged }: Props) {
  const fetchMergeCandidates = useKlantenStore((s) => s.fetchMergeCandidates);
  const mergeCustomer = useKlantenStore((s) => s.mergeCustomer);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MergeCandidatesResult | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [masterId, setMasterId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Partial<Record<keyof MergeAddressFields, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Laad kandidaten bij openen; reset alles bij sluiten.
  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setSelectedCandidateId(null);
      setMasterId(null);
      setOverrides({});
      setSubmitting(false);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMergeCandidates(sourceAccountId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (!res) {
        setError('Kandidaten ophalen mislukt');
        return;
      }
      setData(res);
      const only = res.candidates[0];
      if (res.candidates.length === 1 && only) {
        setSelectedCandidateId(only.id);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, sourceAccountId, fetchMergeCandidates]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  const selectedCandidate = useMemo(
    () => data?.candidates.find((c) => c.id === selectedCandidateId) ?? null,
    [data, selectedCandidateId]
  );

  // Standaard-master = aanbevolen (oudste) als die in het paar zit, anders de oudste van bron/kandidaat.
  useEffect(() => {
    if (!data || !selectedCandidate) {
      setMasterId(null);
      return;
    }
    const pair = [data.source.id, selectedCandidate.id];
    let def: string;
    if (pair.includes(data.recommendedMasterId)) {
      def = data.recommendedMasterId;
    } else {
      def = (data.source.createdDate || '') <= (selectedCandidate.createdDate || '')
        ? data.source.id
        : selectedCandidate.id;
    }
    setMasterId(def);
  }, [data, selectedCandidate]);

  // Master + duplicaat van het paar (bron vs gekozen kandidaat).
  const pair = useMemo(() => {
    if (!data || !selectedCandidate || !masterId) return null;
    const source = {
      id: data.source.id,
      name: data.source.name || sourceName,
      createdDate: data.source.createdDate,
      counts: data.source.counts,
      address: data.source.address,
      blacklisted: false as boolean,
    };
    const cand = {
      id: selectedCandidate.id,
      name: selectedCandidate.name,
      createdDate: selectedCandidate.createdDate,
      counts: selectedCandidate.counts,
      address: selectedCandidate.address,
      blacklisted: selectedCandidate.blacklisted,
    };
    const master = masterId === source.id ? source : cand;
    const duplicate = masterId === source.id ? cand : source;
    return { master, duplicate };
  }, [data, selectedCandidate, masterId, sourceName]);

  // Adresvelden waar het duplicaat een waarde heeft die van de master verschilt.
  const overridableFields = useMemo(() => {
    if (!pair) return [] as (keyof MergeAddressFields)[];
    return ADDRESS_KEYS.filter((f) => {
      const m = (pair.master.address[f] || '').trim();
      const d = (pair.duplicate.address[f] || '').trim();
      return d !== '' && d !== m;
    });
  }, [pair]);

  // Default: vink aan waar de master leeg is (vul het gat uit het duplicaat).
  useEffect(() => {
    if (!pair) {
      setOverrides({});
      return;
    }
    const next: Partial<Record<keyof MergeAddressFields, boolean>> = {};
    for (const f of overridableFields) {
      next[f] = (pair.master.address[f] || '').trim() === '';
    }
    setOverrides(next);
  }, [pair, overridableFields]);

  if (!isOpen) return null;

  function buildFieldOverrides(): Record<string, unknown> {
    if (!pair) return {};
    const out: Record<string, unknown> = {};
    for (const f of overridableFields) {
      if (overrides[f]) out[f] = pair.duplicate.address[f];
    }
    return out;
  }

  async function handleConfirm() {
    if (!pair) return;
    setSubmitting(true);
    setError(null);
    const result = await mergeCustomer(pair.master.id, pair.duplicate.id, buildFieldOverrides());
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error || 'Samenvoegen mislukt');
      return;
    }
    onMerged(result.masterId);
  }

  const candidates = data?.candidates ?? [];

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Klanten samenvoegen</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Voeg een dubbel account samen met <span className="font-medium">{sourceName}</span>.
          </p>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto text-sm text-slate-700">
          {loading && (
            <div className="py-8 text-center text-slate-500">Mogelijke duplicaten zoeken…</div>
          )}

          {!loading && data && candidates.length === 0 && (
            <div className="py-8 text-center text-slate-500">
              Geen mogelijke duplicaten gevonden voor deze klant.
            </div>
          )}

          {/* 1. Kandidaat kiezen */}
          {!loading && candidates.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Mogelijke duplicaten
              </h3>
              <div className="space-y-2">
                {candidates.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                      selectedCandidateId === c.id
                        ? 'border-indigo-400 bg-indigo-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="merge-candidate"
                      className="mt-1"
                      checked={selectedCandidateId === c.id}
                      onChange={() => setSelectedCandidateId(c.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">{c.name || '(zonder naam)'}</span>
                        {c.blacklisted && (
                          <span className="shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                            Blacklist
                          </span>
                        )}
                        {c.signal && (
                          <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                            {SIGNAL_LABELS[c.signal]}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        Aangemaakt {c.createdDate ? formatDate(c.createdDate) : '—'} · {countsLine(c.counts)}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* 2. Master kiezen */}
          {pair && data && selectedCandidate && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Welk account blijft bestaan (master)?
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: data.source.id, name: data.source.name || sourceName, createdDate: data.source.createdDate, counts: data.source.counts },
                  { id: selectedCandidate.id, name: selectedCandidate.name, createdDate: selectedCandidate.createdDate, counts: selectedCandidate.counts },
                ].map((party) => (
                  <label
                    key={party.id}
                    className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                      masterId === party.id
                        ? 'border-indigo-400 bg-indigo-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="merge-master"
                      className="mt-1"
                      checked={masterId === party.id}
                      onChange={() => setMasterId(party.id)}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-slate-900 truncate">{party.name || '(zonder naam)'}</span>
                        {data.recommendedMasterId === party.id && (
                          <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                            Aanbevolen
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {party.createdDate ? formatDate(party.createdDate) : '—'} · {countsLine(party.counts)}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* 3. Survivorship-preview (adres) */}
          {pair && overridableFields.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Adres — afwijkende velden
              </h3>
              <div className="space-y-1.5">
                {overridableFields.map((f) => (
                  <label key={f} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={overrides[f] === true}
                      onChange={(e) => setOverrides((prev) => ({ ...prev, [f]: e.target.checked }))}
                    />
                    <div className="min-w-0 flex-1 text-xs">
                      <span className="font-medium text-slate-600">{ADDRESS_LABELS[f]}:</span>{' '}
                      <span className="text-slate-400 line-through">{pair.master.address[f] || '(leeg)'}</span>{' '}
                      <span className="text-slate-400">→</span>{' '}
                      <span className="text-slate-900">{pair.duplicate.address[f] || '(leeg)'}</span>
                    </div>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                Aangevinkt = waarde van het samengevoegde account overnemen. Niet aangevinkt = master behoudt z'n eigen waarde.
              </p>
            </div>
          )}

          {/* 4. Waarschuwing */}
          {pair && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <span className="font-medium">{pair.duplicate.name || 'Het andere account'}</span> gaat naar de
              prullenbak (15 dagen herstelbaar). Alle orders, facturen, cases en bestanden verhuizen naar{' '}
              <span className="font-medium">{pair.master.name || 'de master'}</span>.
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !pair}
            className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {submitting ? 'Samenvoegen…' : 'Samenvoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
